import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives, vModelSelect as _vModelSelect, withModifiers as _withModifiers, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "document-annotator" }
const _hoisted_2 = {
  key: 0,
  class: "mobile-notice"
}
const _hoisted_3 = { class: "mobile-notice__title" }
const _hoisted_4 = { class: "mobile-notice__message" }
const _hoisted_5 = { class: "annotator-header" }
const _hoisted_6 = { class: "annotator-header__left" }
const _hoisted_7 = { class: "annotator-header__title" }
const _hoisted_8 = {
  key: 0,
  class: "annotator-header__status"
}
const _hoisted_9 = { class: "annotator-header__actions" }
const _hoisted_10 = ["title"]
const _hoisted_11 = { class: "annotator-main" }
const _hoisted_12 = {
  key: 0,
  class: "annotator-loading"
}
const _hoisted_13 = {
  key: 1,
  class: "annotator-error"
}
const _hoisted_14 = {
  key: 2,
  class: "page-indicator"
}
const _hoisted_15 = { class: "dialog-header" }
const _hoisted_16 = { style: {"color":"#333 !important"} }
const _hoisted_17 = {
  class: "calibration-dialog-content",
  style: {"background":"#fff !important","color":"#333 !important"}
}
const _hoisted_18 = {
  class: "calibration-instruction-text",
  style: {"color":"#555 !important"}
}
const _hoisted_19 = { class: "calibration-form-group" }
const _hoisted_20 = { style: {"color":"#222 !important"} }
const _hoisted_21 = { class: "calibration-input-group" }
const _hoisted_22 = ["placeholder"]
const _hoisted_23 = {
  value: "meters",
  style: {"background":"#fff !important","color":"#222 !important"}
}
const _hoisted_24 = {
  value: "feet",
  style: {"background":"#fff !important","color":"#222 !important"}
}
const _hoisted_25 = {
  key: 0,
  class: "calibration-detected-scale",
  style: {"background":"#e3f2fd !important","color":"#1565c0 !important","border":"1px solid #90caf9 !important"}
}
const _hoisted_26 = { style: {"color":"#1565c0 !important"} }
const _hoisted_27 = { style: {"color":"#0d47a1 !important"} }
const _hoisted_28 = { class: "dialog-footer" }
const _hoisted_29 = {
  key: 2,
  class: "calibration-overlay"
}
const _hoisted_30 = { class: "calibration-instruction" }
const _hoisted_31 = { key: 0 }
const _hoisted_32 = { key: 1 }

import { ref, onMounted, onUnmounted, computed } from 'vue';
// @ts-ignore
import { Canvas, Image, Rect, Circle, Line, IText, Text, Polygon, Group, PencilBrush, util } from 'fabric';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/composables/useStore';
import { useNotifications } from '@/composables/useNotifications';
import { useIsMobile } from '@/composables/useIsMobile';
import AppButton from '@/components/base/AppButton.vue';
import AnnotationToolbar from './AnnotationToolbar.vue';
import AnnotationContextPanel from './AnnotationContextPanel.vue';
import AnnotationControls from './AnnotationControls.vue';
import PDFPageNavigator from './PDFPageNavigator.vue';
import AnnotationListPanel from './AnnotationListPanel.vue';
import MeasurementHistoryPanel from './MeasurementHistoryPanel.vue';
import LayerPanel from './LayerPanel.vue';
import AnnotationService from '@/services/annotation.service';
import { AnnotationTool, type AnnotationToolSettings, type AnnotationListItem, type MultiPageAnnotation, type AnnotationLayer } from '@/types/annotation';
import { saveAs } from 'file-saver';

interface Props {
  projectId: number;
  documentId: number;
  documentUrl: string;
  fileName: string;
  fileType: string;
}

const maxHistorySize = 50;

// Drawing state

export default /*@__PURE__*/_defineComponent({
  __name: 'DocumentAnnotator',
  props: {
    projectId: {},
    documentId: {},
    documentUrl: {},
    fileName: {},
    fileType: {}
  },
  emits: ["close", "saved"],
  setup(__props: any, { emit: __emit }) {

// @ts-ignore
const props = __props;
const emit = __emit;

const { t } = useI18n();
const store = useStore();
const { isMobile } = useIsMobile();
const { notifyConfirm, notifySuccess } = useNotifications();

// Refs
const fabricCanvas = ref<HTMLCanvasElement | null>(null);
const canvasContainer = ref<HTMLDivElement | null>(null);
const canvas = ref<Canvas | null>(null);
const backgroundImage = ref<Image | null>(null);
const toolbarRef = ref<InstanceType<typeof AnnotationToolbar> | null>(null);
const toolbarElement = ref<HTMLElement | null>(null);

// State
const loading = ref(true);
const error = ref<string | null>(null);
const saving = ref(false);
const isDirty = ref(false);
const currentTool = ref<AnnotationTool>(AnnotationTool.SELECT);
const zoomLevel = ref(1);
const showContextPanel = ref(true); // Show by default when tool is active
const showZoomPanel = ref(true); // Show zoom panel by default
const canUndo = ref(false);
const canRedo = ref(false);

// Tool settings
const toolSettings = ref<AnnotationToolSettings>({
  strokeColor: '#FF0000',
  fillColor: '#FF000040',
  strokeWidth: 2,
  fontSize: 16,
  opacity: 1,
  scale: '1:100',
  scaleRatio: 100,
  measurementUnit: 'meters',
  showGrid: false,
  gridSize: 20,
});

// Multi-page PDF state
let pdfDocumentInstance: any = null; // Don't use ref for PDF.js objects
const totalPages = ref(1);
const currentPage = ref(1);
const pageAnnotations = ref<Record<number, string>>({});
const annotationCounts = ref<Record<number, number>>({});

// Annotation list state
const showAnnotationList = ref(false);
const annotationListItems = ref<AnnotationListItem[]>([]);
const selectedAnnotationId = ref<string | undefined>(undefined);

// Measurement history state
const showMeasurementHistory = ref(false);
const measurementHistory = ref<Array<{
  type: 'distance' | 'area' | 'polygon';
  value: string;
  pageNumber?: number;
  objectId?: string;
}>>([]);
const selectedMeasurementIndex = ref<number | undefined>(undefined);

// Layer system state
const showLayerPanel = ref(false);
const layers = ref<AnnotationLayer[]>([]);
const activeLayerId = ref<string | null>(null);

// Scale calibration state
const isCalibrating = ref(false);
const calibrationStep = ref<'idle' | 'first-point' | 'second-point' | 'enter-distance'>('idle');
const calibrationPoints = ref<Array<{ x: number; y: number }>>([]);
const calibrationLine = ref<any>(null);
const calibrationMarkers = ref<any[]>([]);
const showCalibrationDialog = ref(false);
const calibrationDistance = ref<string>('');
const calibrationUnit = ref<'meters' | 'feet'>('meters');

// History management
const historyStack = ref<string[]>([]);
const historyIndex = ref(-1);
let isDrawing = false;
let drawingObject: any = null;
let startPoint = { x: 0, y: 0 };

// Auto-save timer
let autoSaveTimer: ReturnType<typeof setTimeout> | null = null;

// Helper function to ensure background is always present
const ensureBackgroundImage = () => {
  if (backgroundImage.value && canvas.value && canvas.value.backgroundImage !== backgroundImage.value) {
    console.log('Restoring background image');
    canvas.value.backgroundImage = backgroundImage.value;
    canvas.value.renderAll();
  }
};

// Initialize canvas
const initializeCanvas = async () => {
  try {
    loading.value = true;
    error.value = null;

    if (!fabricCanvas.value || !canvasContainer.value) {
      throw new Error('Canvas elements not found');
    }

    // Create Fabric canvas
    canvas.value = new Canvas(fabricCanvas.value, {
      backgroundColor: '#f5f5f5',
      selection: true,
      preserveObjectStacking: true,
    });


    // Set canvas size
    resizeCanvas();

    // Load document as background
    await loadDocumentBackground();
    
    console.log('Background loaded:', {
      hasBackground: !!canvas.value.backgroundImage,
      backgroundImage: backgroundImage.value,
      canvasWidth: canvas.value.getWidth(),
      canvasHeight: canvas.value.getHeight()
    });

    // Initialize layer system
    initializeLayers();

    // Load existing annotations
    await loadAnnotations();
    
    // Ensure background is still set after loading annotations
    if (backgroundImage.value && canvas.value && !canvas.value.backgroundImage) {
      console.warn('Background was lost after loading annotations, restoring...');
      canvas.value.backgroundImage = backgroundImage.value;
      canvas.value.renderAll();
    }

    // Setup event listeners
    setupCanvasEvents();
    setupKeyboardShortcuts();

    // brush may be configured when switching tools

    loading.value = false;
    
    // Ensure background is visible after all initialization
    ensureBackgroundImage();
    
    // Set up a background checker for the first 2 seconds to catch any timing issues
    let checkCount = 0;
    const backgroundChecker = setInterval(() => {
      if (checkCount++ < 10) {
        ensureBackgroundImage();
      } else {
        clearInterval(backgroundChecker);
      }
    }, 200);
  } catch (err: any) {
    console.error('Error initializing canvas:', err);
    error.value = err.message || t('annotation.initError');
    loading.value = false;
  }
};

// Load document as background image
const loadDocumentBackground = async () => {
  // Check if it's a PDF file
  const isPdf = props.fileType.toLowerCase() === 'pdf' || 
                props.fileName.toLowerCase().endsWith('.pdf');
  
  if (isPdf) {
    return loadPdfBackground();
  } else {
    // For non-PDF files, single page only
    totalPages.value = 1;
    return loadImageBackground();
  }
};

// Load PDF as background
const loadPdfBackground = async () => {
  try {
    // Dynamically import pdfjsLib only when needed
    const { getDocument, GlobalWorkerOptions } = await import('pdfjs-dist');
    
    // Set worker source - use local worker file from node_modules
    GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.mjs',
      import.meta.url
    ).toString();

    // Load PDF document with proper configuration
    const loadingTask = getDocument({
      url: props.documentUrl,
      cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/',
      cMapPacked: true,
    });
    const pdf = await loadingTask.promise;

    // Save PDF document reference and total pages
    pdfDocumentInstance = pdf;
    totalPages.value = pdf.numPages;

    // Load the current page
    await loadPdfPage(currentPage.value);
  } catch (err) {
    console.error('Error loading PDF:', err);
    throw new Error('Failed to load PDF document');
  }
};

// Load a specific PDF page
const loadPdfPage = async (pageNum: number) => {
  if (!pdfDocumentInstance || !canvas.value) return;

  try {
    const page = await pdfDocumentInstance.getPage(pageNum);
    
    // Calculate scale to fit container
    const containerWidth = canvasContainer.value?.clientWidth || 800;
    const containerHeight = canvasContainer.value?.clientHeight || 600;
    const viewport = page.getViewport({ scale: 1.0 });
    
    const scale = Math.min(
      (containerWidth * 0.9) / viewport.width,
      (containerHeight * 0.9) / viewport.height
    );
    
    // Use higher DPI for better quality (2x or 3x for retina displays)
    const outputScale = window.devicePixelRatio || 1;
    const highQualityScale = scale * Math.max(2, outputScale);
    
    const scaledViewport = page.getViewport({ scale: highQualityScale });

    // Create a temporary canvas to render the PDF at high resolution
    const tempCanvas = document.createElement('canvas');
    const context = tempCanvas.getContext('2d');
    if (!context) throw new Error('Could not get canvas context');

    tempCanvas.width = scaledViewport.width;
    tempCanvas.height = scaledViewport.height;

    // Render PDF page to canvas at high resolution
    await page.render({
      canvasContext: context,
      viewport: scaledViewport
    }).promise;

    // Convert canvas to image and add to fabric canvas
    const imageUrl = tempCanvas.toDataURL('image/png');
    
    return new Promise<void>((resolve, reject) => {
      Image.fromURL(
        imageUrl,
        { crossOrigin: 'anonymous' }
      ).then((img: Image) => {
        if (!canvas.value) return reject(new Error('Canvas not initialized'));

        backgroundImage.value = img;
        img.set({
          selectable: false,
          evented: false,
          left: 0,
          top: 0,
        });

        canvas.value.setWidth(img.width!);
        canvas.value.setHeight(img.height!);
        canvas.value.backgroundImage = img;
        canvas.value.renderAll();

        resolve();
      }).catch(reject);
    });
  } catch (err) {
    console.error('Error loading PDF page:', err);
    throw err;
  }
};

// Load regular image as background
const loadImageBackground = async () => {
  return new Promise<void>((resolve, reject) => {
    Image.fromURL(
      props.documentUrl,
      { crossOrigin: 'anonymous' }
    ).then((img: Image) => {
      if (!canvas.value) return reject(new Error('Canvas not initialized'));

      backgroundImage.value = img;
      img.set({
        selectable: false,
        evented: false,
        left: 0,
        top: 0,
      });

      // Scale image to fit canvas
      const containerWidth = canvasContainer.value?.clientWidth || 800;
      const containerHeight = canvasContainer.value?.clientHeight || 600;
      const scale = Math.min(
        (containerWidth * 0.9) / img.width!,
        (containerHeight * 0.9) / img.height!
      );

      img.scale(scale);
      canvas.value.setWidth(img.width! * scale);
      canvas.value.setHeight(img.height! * scale);
      canvas.value.backgroundImage = img;
      canvas.value.renderAll();

      resolve();
    }).catch(reject);
  });
};

// Load existing annotations
const loadAnnotations = async () => {
  try {
    const annotation = await AnnotationService.getLatestAnnotation(
      props.projectId,
      props.documentId
    );

    if (annotation && annotation.annotationData && canvas.value) {
      console.log('Loading existing annotations...');
      const data = JSON.parse(annotation.annotationData);
      
      // Check if data has the new format with settings
      if (data.pages && Array.isArray(data.pages)) {
        // New multi-page format with settings
        data.pages.forEach((pageData: MultiPageAnnotation) => {
          pageAnnotations.value[pageData.pageNumber] = pageData.annotationData;
        });
        
        // Load settings if available
        if (data.settings) {
          console.log('Loading saved scale settings:', data.settings);
          toolSettings.value = { ...toolSettings.value, ...data.settings };
        }
        
        // Load layer configuration if available
        if (data.layers && Array.isArray(data.layers)) {
          console.log('Loading saved layer configuration');
          layers.value = data.layers.map((l: any) => ({
            ...l,
            createdAt: new Date(l.createdAt || Date.now())
          }));
          if (data.activeLayerId) {
            activeLayerId.value = data.activeLayerId;
          }
        }
        
        // Load current page annotations
        if (pageAnnotations.value[currentPage.value]) {
          const currentPageData = JSON.parse(pageAnnotations.value[currentPage.value]);
          // Store background before loading to avoid losing it
          const bgImage = backgroundImage.value;
          canvas.value.loadFromJSON(currentPageData, () => {
            if (bgImage && canvas.value) {
              canvas.value.backgroundImage = bgImage;
              canvas.value.renderAll();
            }
            console.log('Annotations loaded successfully');
            saveHistory();
            updateAnnotationList();
            updateAnnotationCounts();
            updateLayerObjectCounts();
          });
        } else {
          // No annotations for this page, just save initial state
          // Make sure background is set before saving history
          if (backgroundImage.value && canvas.value) {
            canvas.value.backgroundImage = backgroundImage.value;
            canvas.value.renderAll();
          }
          saveHistory();
        }
      } else if (data.canvasData) {
        // New single-page format with settings
        if (data.settings) {
          console.log('Loading saved scale settings:', data.settings);
          toolSettings.value = { ...toolSettings.value, ...data.settings };
        }
        
        // Load layer configuration if available
        if (data.layers && Array.isArray(data.layers)) {
          console.log('Loading saved layer configuration');
          layers.value = data.layers.map((l: any) => ({
            ...l,
            createdAt: new Date(l.createdAt || Date.now())
          }));
          if (data.activeLayerId) {
            activeLayerId.value = data.activeLayerId;
          }
        }
        
        // Store background before loading to avoid losing it
        const bgImage = backgroundImage.value;
        canvas.value.loadFromJSON(JSON.parse(data.canvasData), () => {
          if (bgImage && canvas.value) {
            canvas.value.backgroundImage = bgImage;
            canvas.value.renderAll();
          }
          console.log('Annotations loaded successfully');
          saveHistory();
          updateAnnotationList();
          updateLayerObjectCounts();
        });
      } else if (Array.isArray(data)) {
        // Legacy multi-page format (without settings)
        data.forEach((pageData: MultiPageAnnotation) => {
          pageAnnotations.value[pageData.pageNumber] = pageData.annotationData;
        });
        
        // Load current page annotations
        if (pageAnnotations.value[currentPage.value]) {
          const currentPageData = JSON.parse(pageAnnotations.value[currentPage.value]);
          // Store background before loading to avoid losing it
          const bgImage = backgroundImage.value;
          canvas.value.loadFromJSON(currentPageData, () => {
            if (bgImage && canvas.value) {
              canvas.value.backgroundImage = bgImage;
              canvas.value.renderAll();
            }
            console.log('Annotations loaded successfully (legacy format)');
            saveHistory();
            updateAnnotationList();
            updateAnnotationCounts();
            updateLayerObjectCounts();
          });
        } else {
          // No annotations for this page, just save initial state
          // Make sure background is set before saving history
          if (backgroundImage.value && canvas.value) {
            canvas.value.backgroundImage = backgroundImage.value;
            canvas.value.renderAll();
          }
          saveHistory();
        }
      } else {
        // Legacy single-page format (without settings)
        // Store background before loading to avoid losing it
        const bgImage = backgroundImage.value;
        canvas.value.loadFromJSON(data, () => {
          if (bgImage && canvas.value) {
            canvas.value.backgroundImage = bgImage;
            canvas.value.renderAll();
          }
          console.log('Annotations loaded successfully (legacy format)');
          saveHistory();
          updateAnnotationList();
          updateLayerObjectCounts();
        });
      }
    } else {
      console.log('No existing annotations found - starting fresh');
      // Make sure background is set before saving history
      if (backgroundImage.value && canvas.value) {
        canvas.value.backgroundImage = backgroundImage.value;
        canvas.value.renderAll();
      }
      saveHistory();
    }
  } catch (err) {
    console.warn('Error loading annotations (will start fresh):', err);
    // Continue without annotations - this is OK for first-time annotation
    saveHistory();
  }
};

// Setup canvas event listeners
const setupCanvasEvents = () => {
  if (!canvas.value) return;

  // Mouse events for drawing
  canvas.value.on('mouse:down', handleMouseDown);
  canvas.value.on('mouse:move', handleMouseMove);
  canvas.value.on('mouse:up', handleMouseUp);

  // Object events for history
  canvas.value.on('object:modified', () => {
    saveHistory();
    markDirty();
    updateAnnotationList();
  });
  canvas.value.on('object:added', () => {
    // Always update layer counts when objects are added
    updateLayerObjectCounts();
    if (!isDrawing) {
      saveHistory();
      markDirty();
      updateAnnotationList();
    }
  });
  canvas.value.on('object:removed', () => {
    saveHistory();
    markDirty();
    updateAnnotationList();
    updateLayerObjectCounts();
  });

  // Mouse wheel for zoom
  canvas.value.on('mouse:wheel', handleMouseWheel);

  // When Fabric creates a freehand path, update history and mark dirty
  canvas.value.on('path:created', (e: any) => {
    // Assign layer to the newly created path
    if (e.path) {
      assignLayerToObject(e.path);
    }
    saveHistory();
    markDirty();
    updateAnnotationList();
    updateLayerObjectCounts();
  });
  
};

// Handle mouse down
const handleMouseDown = (options: any) => {
  if (!canvas.value) return;

  let pointer = canvas.value.getPointer(options.e);

  // Handle calibration clicks
  if (isCalibrating.value && (calibrationStep.value === 'first-point' || calibrationStep.value === 'second-point')) {
    handleCalibrationClick(pointer);
    return;
  }

  // If we're in Select mode, let Fabric handle selection/interaction
  if (currentTool.value === AnnotationTool.SELECT) return;

  // Handle clicking on existing text objects to enter edit mode
  const target = (options && options.target) ? options.target : null;
  if (currentTool.value === AnnotationTool.TEXT && target && (target.type === 'i-text' || target.type === 'text')) {
    // If clicking on a text object, enter editing mode
    canvas.value.setActiveObject(target);
    target.enterEditing();
    canvas.value.renderAll();
    return;
  }

  // Handle eraser tool - click on an object to delete it
  if (currentTool.value === AnnotationTool.ERASER && target) {
    // Don't erase grid, background, or non-user objects
    if (target.selectable !== false && !target.isGrid && target !== backgroundImage.value) {
      // Deselect the object first to avoid rendering issues
      canvas.value.discardActiveObject();
      
      // If it's a measurement, remove associated labels and markers first
      if ((target as any).measurement) {
        const objects = canvas.value.getObjects();
        const measurementId = target.id;
        // Remove associated labels and markers
        objects.forEach((obj: any) => {
          if (obj.measurementLabel === measurementId || obj.measurementMarker === measurementId || 
              obj.measurementEndpoint === measurementId) {
            canvas.value!.remove(obj);
          }
        });
      }
      
      // Remove the clicked object
      canvas.value.remove(target);
      
      // Render before saving to ensure clean state
      canvas.value.renderAll();
      
      // Save state to history and mark dirty
      saveHistory();
      markDirty();
    }
    return;
  }

  isDrawing = true;
  startPoint = { x: pointer.x, y: pointer.y };

  switch (currentTool.value) {
    case AnnotationTool.FREEHAND:
      startFreehandDrawing(pointer);
      break;
    case AnnotationTool.RECTANGLE:
    case AnnotationTool.CIRCLE:
    case AnnotationTool.LINE:
    case AnnotationTool.ARROW:
      startShapeDrawing(pointer);
      break;
    case AnnotationTool.HIGHLIGHT:
      startHighlight(pointer);
      break;
    case AnnotationTool.TEXT:
      addText();
      break;
    case AnnotationTool.MEASURE_DISTANCE:
      startDistanceMeasurement(pointer);
      break;
    case AnnotationTool.MEASURE_AREA:
      startAreaMeasurement(pointer);
      break;
    case AnnotationTool.MEASURE_POLYGON:
      startPolygonMeasurement(pointer);
      return; // Don't set isDrawing for polygon (multi-click)
  }
};

// Handle mouse move
const handleMouseMove = (options: any) => {
  if (!canvas.value || !isDrawing || !drawingObject) return;

  const pointer = canvas.value.getPointer(options.e);

  switch (currentTool.value) {
    case AnnotationTool.RECTANGLE:
      updateRectangle(pointer);
      break;
    case AnnotationTool.CIRCLE:
      updateCircle(pointer);
      break;
    case AnnotationTool.LINE:
    case AnnotationTool.ARROW:
      updateLine(pointer);
      break;
    case AnnotationTool.HIGHLIGHT:
      updateRectangle(pointer);
      break;
    case AnnotationTool.MEASURE_DISTANCE:
      updateDistanceMeasurement(pointer);
      break;
    case AnnotationTool.MEASURE_AREA:
      updateAreaMeasurement(pointer);
      break;
  }

  canvas.value.renderAll();
};

// Handle mouse up
const handleMouseUp = () => {
  if (!canvas.value) return;

  isDrawing = false;
  
  if (drawingObject) {
    // Track measurement if it's a measurement tool
    if ((drawingObject as any).measurement) {
      const measurementType = (drawingObject as any).measurement;
      // Find the label text to get the measurement value
      const objects = canvas.value.getObjects();
      const label = objects.find((obj: any) => obj.measurementLabel === drawingObject.id);
      if (label && (label as any).text) {
        const objectId = drawingObject.id || `measurement-${Date.now()}`;
        drawingObject.id = objectId;
        addMeasurementToHistory(measurementType, (label as any).text, objectId);
      }
    }
    
    canvas.value.setActiveObject(drawingObject);
    drawingObject = null;
    saveHistory();
    markDirty();
  }

  canvas.value.isDrawingMode = false;
};

// Start freehand drawing
const startFreehandDrawing = (pointer: any) => {
  if (!canvas.value) return;
  // Ensure selection is disabled while drawing freehand
  canvas.value.selection = false;

  // Configure brush appearance (don't toggle isDrawingMode here — it must be set before the mousedown event)
  try {
    const currentBrush = (canvas.value as any).freeDrawingBrush;
    if (!currentBrush || !currentBrush.constructor) {
      (canvas.value as any).freeDrawingBrush = new PencilBrush(canvas.value);
    }

    const brush = (canvas.value as any).freeDrawingBrush;
    brush.color = toolSettings.value.strokeColor;
    brush.width = toolSettings.value.strokeWidth;
    canvas.value.freeDrawingCursor = 'crosshair';
  } catch (err) {
    // Best-effort brush configuration
    try {
      if ((canvas.value as any).freeDrawingBrush) {
        (canvas.value as any).freeDrawingBrush.color = toolSettings.value.strokeColor;
        (canvas.value as any).freeDrawingBrush.width = toolSettings.value.strokeWidth;
      }
    } catch (e) {
      console.warn('Could not configure freeDrawingBrush during startFreehandDrawing', e);
    }
  }
};

// Start shape drawing
const startShapeDrawing = (pointer: any) => {
  if (!canvas.value) return;

  canvas.value.selection = false;

  switch (currentTool.value) {
    case AnnotationTool.RECTANGLE:
      drawingObject = new Rect({
        left: pointer.x,
        top: pointer.y,
        width: 0,
        height: 0,
        fill: toolSettings.value.fillColor,
        stroke: toolSettings.value.strokeColor,
        strokeWidth: toolSettings.value.strokeWidth,
      });
      break;
    case AnnotationTool.CIRCLE:
      drawingObject = new Circle({
        left: pointer.x,
        top: pointer.y,
        radius: 0,
        fill: toolSettings.value.fillColor,
        stroke: toolSettings.value.strokeColor,
        strokeWidth: toolSettings.value.strokeWidth,
      });
      break;
    case AnnotationTool.LINE:
    case AnnotationTool.ARROW:
      drawingObject = new Line(
        [pointer.x, pointer.y, pointer.x, pointer.y],
        {
          stroke: toolSettings.value.strokeColor,
          strokeWidth: toolSettings.value.strokeWidth,
        }
      );
      break;
    case AnnotationTool.HIGHLIGHT:
      drawingObject = new Rect({
        left: pointer.x,
        top: pointer.y,
        width: 0,
        height: 0,
        fill: toolSettings.value.strokeColor + '40', // Semi-transparent
        stroke: 'transparent',
        strokeWidth: 0,
      });
      break;
  }

  if (drawingObject) {
    assignLayerToObject(drawingObject);
    canvas.value.add(drawingObject);
  }
};

// Update rectangle during drawing
const updateRectangle = (pointer: any) => {
  if (!drawingObject) return;

  const width = pointer.x - startPoint.x;
  const height = pointer.y - startPoint.y;

  drawingObject.set({
    width: Math.abs(width),
    height: Math.abs(height),
    left: width > 0 ? startPoint.x : pointer.x,
    top: height > 0 ? startPoint.y : pointer.y,
  });
};

// Update circle during drawing
const updateCircle = (pointer: any) => {
  if (!drawingObject) return;

  const radius = Math.sqrt(
    Math.pow(pointer.x - startPoint.x, 2) + Math.pow(pointer.y - startPoint.y, 2)
  );

  drawingObject.set({ radius: radius / 2 });
};

// Update line during drawing
const updateLine = (pointer: any) => {
  if (!drawingObject) return;

  drawingObject.set({
    x2: pointer.x,
    y2: pointer.y,
  });
};

// Start highlight
const startHighlight = (pointer: any) => {
  startShapeDrawing(pointer);
  // Drawing object is already created with proper highlight properties in startShapeDrawing
};

// Add text annotation
const addText = () => {
  if (!canvas.value) return;

  canvas.value.selection = false;

  // Create an IText object at the click position
  const text = new IText('Text', {
    left: startPoint.x,
    top: startPoint.y,
    fontFamily: 'Arial',
    fontSize: toolSettings.value.fontSize,
    fill: toolSettings.value.strokeColor,
    editable: true,
  });

  // Assign layer to the text object
  assignLayerToObject(text);
  
  // Add to canvas
  canvas.value.add(text);
  
  // Make it active and enter editing mode
  canvas.value.setActiveObject(text);
  text.enterEditing();
  text.selectAll();
  
  // Save to history and mark dirty
  saveHistory();
  markDirty();
  
  canvas.value.renderAll();
};

// Start distance measurement
const startDistanceMeasurement = (pointer: any) => {
  if (!canvas.value) return;

  canvas.value.selection = false;

  // Generate unique ID for this measurement
  const measurementId = `measurement-${Date.now()}-${Math.random()}`;

  const line = new Line(
    [pointer.x, pointer.y, pointer.x, pointer.y],
    {
      stroke: '#007bff',              // Professional blue
      strokeWidth: 2,
      strokeDashArray: [5, 5],        // Dashed pattern
      selectable: true,
      hasControls: false,
    } as any
  );
  (line as any).measurement = 'distance';
  (line as any).id = measurementId;

  drawingObject = line;
  assignLayerToObject(line, true); // true = measurement
  canvas.value.add(line);
  
  // Add start endpoint marker
  const startMarker = new Circle({
    left: pointer.x,
    top: pointer.y,
    radius: 4,
    fill: '#007bff',
    stroke: 'white',
    strokeWidth: 2,
    originX: 'center',
    originY: 'center',
    selectable: false,
    evented: false,
  } as any);
  (startMarker as any).measurementEndpoint = measurementId;
  (startMarker as any).isStartMarker = true;
  assignLayerToObject(startMarker, true); // true = measurement
  canvas.value.add(startMarker);
};

// Update distance measurement
const updateDistanceMeasurement = (pointer: any) => {
  if (!drawingObject || !canvas.value) return;

  drawingObject.set({
    x2: pointer.x,
    y2: pointer.y,
  });

  // Calculate distance
  const pixelDistance = Math.sqrt(
    Math.pow(pointer.x - startPoint.x, 2) + 
    Math.pow(pointer.y - startPoint.y, 2)
  );

  // Convert to real-world distance using scale
  const scaleRatio = toolSettings.value.scaleRatio || 100;
  const realDistance = (pixelDistance / 100) * scaleRatio; // Assuming 100 pixels = 1 unit at scale

  // Add measurement label
  const unit = toolSettings.value.measurementUnit === 'feet' ? 'ft' : 'm';
  const label = `${realDistance.toFixed(2)} ${unit}`;

  const measurementId = (drawingObject as any).id;

  // Remove old label and endpoint marker if exists
  const objects = canvas.value.getObjects();
  const oldLabels = objects.filter((obj: any) => obj.measurementLabel === measurementId);
  const oldEndMarkers = objects.filter((obj: any) => 
    obj.measurementEndpoint === measurementId && !obj.isStartMarker
  );
  
  // Remove all old labels and end markers
  oldLabels.forEach((obj: any) => canvas.value!.remove(obj));
  oldEndMarkers.forEach((obj: any) => canvas.value!.remove(obj));
  
  // Add end endpoint marker
  const endMarker = new Circle({
    left: pointer.x,
    top: pointer.y,
    radius: 4,
    fill: '#007bff',
    stroke: 'white',
    strokeWidth: 2,
    originX: 'center',
    originY: 'center',
    selectable: false,
    evented: false,
  } as any);
  (endMarker as any).measurementEndpoint = measurementId;
  (endMarker as any).isStartMarker = false;
  assignLayerToObject(endMarker, true); // true = measurement
  canvas.value.add(endMarker);

  // Add new label with enhanced visibility
  const midX = (startPoint.x + pointer.x) / 2;
  const midY = (startPoint.y + pointer.y) / 2;
  
  const text = new Text(label, {
    left: midX,
    top: midY - 20,
    fontSize: 16,                              // Larger for readability
    fill: '#000000',                           // Black text for maximum contrast
    backgroundColor: 'rgba(255, 255, 255, 0.9)', // White semi-transparent background
    padding: 4,                                 // Padding around text
    selectable: false,
    evented: false,
    shadow: 'rgba(0,0,0,0.3) 0px 2px 4px',    // Subtle shadow for depth
  } as any);
  (text as any).measurementLabel = measurementId;

  assignLayerToObject(text, true); // true = measurement
  canvas.value.add(text);
};

// Start area measurement
const startAreaMeasurement = (pointer: any) => {
  if (!canvas.value) return;

  canvas.value.selection = false;

  // Generate unique ID for this measurement
  const measurementId = `measurement-${Date.now()}-${Math.random()}`;

  const rect = new Rect({
    left: pointer.x,
    top: pointer.y,
    width: 0,
    height: 0,
    fill: 'rgba(0, 123, 255, 0.2)',  // Light blue 20% opacity
    stroke: '#007bff',                // Professional blue
    strokeWidth: 2,
    strokeDashArray: [5, 5],          // Dashed pattern
  } as any);
  (rect as any).measurement = 'area';
  (rect as any).id = measurementId;

  drawingObject = rect;
  assignLayerToObject(rect, true); // true = measurement
  canvas.value.add(rect);
};

// Update area measurement
const updateAreaMeasurement = (pointer: any) => {
  if (!drawingObject || !canvas.value) return;

  const width = pointer.x - startPoint.x;
  const height = pointer.y - startPoint.y;

  drawingObject.set({
    width: Math.abs(width),
    height: Math.abs(height),
    left: width > 0 ? startPoint.x : pointer.x,
    top: height > 0 ? startPoint.y : pointer.y,
  });

  // Calculate area
  const pixelArea = Math.abs(width * height);
  const scaleRatio = toolSettings.value.scaleRatio || 100;
  const realArea = (pixelArea / 10000) * (scaleRatio * scaleRatio); // Convert to real-world area
  
  // Calculate perimeter
  const pixelPerimeter = 2 * (Math.abs(width) + Math.abs(height));
  const realPerimeter = (pixelPerimeter / 100) * scaleRatio;

  // Add measurement label with both area and perimeter
  const unit = toolSettings.value.measurementUnit === 'feet' ? 'ft' : 'm';
  const label = `Area: ${realArea.toFixed(2)} ${unit}²\nPerimeter: ${realPerimeter.toFixed(2)} ${unit}`;

  const measurementId = (drawingObject as any).id;

  // Remove all old labels with this measurement ID
  const objects = canvas.value.getObjects();
  const oldLabels = objects.filter((obj: any) => obj.measurementLabel === measurementId);
  oldLabels.forEach((obj: any) => canvas.value!.remove(obj));

  // Add new label with enhanced visibility
  const text = new Text(label, {
    left: drawingObject.left! + (drawingObject.width! / 2),
    top: drawingObject.top! + (drawingObject.height! / 2),
    fontSize: 16,                              // Larger for readability
    fill: '#000000',                           // Black text for maximum contrast
    backgroundColor: 'rgba(255, 255, 255, 0.9)', // White semi-transparent background
    padding: 4,                                 // Padding around text
    selectable: false,
    evented: false,
    originX: 'center',
    originY: 'center',
    shadow: 'rgba(0,0,0,0.3) 0px 2px 4px',    // Subtle shadow for depth
  } as any);
  (text as any).measurementLabel = measurementId;

  assignLayerToObject(text, true); // true = measurement
  canvas.value.add(text);
};

// Polygon measurement state
let polygonPoints: any[] = [];
let polygonLines: any[] = [];
let polygonMarkers: any[] = [];

// Start polygon measurement
const startPolygonMeasurement = (pointer: any) => {
  if (!canvas.value) return;

  // Add point to polygon
  polygonPoints.push({ x: pointer.x, y: pointer.y });

  // Add marker for this point
  const marker = new Circle({
    left: pointer.x,
    top: pointer.y,
    radius: 4,
    fill: '#007bff',
    stroke: 'white',
    strokeWidth: 2,
    originX: 'center',
    originY: 'center',
    selectable: false,
    evented: false,
  } as any);
  (marker as any).polygonMarker = true;
  polygonMarkers.push(marker);
  assignLayerToObject(marker, true); // true = measurement
  canvas.value.add(marker);

  // Draw line from previous point
  if (polygonPoints.length > 1) {
    const prevPoint = polygonPoints[polygonPoints.length - 2];
    const line = new Line(
      [prevPoint.x, prevPoint.y, pointer.x, pointer.y],
      {
        stroke: '#007bff',
        strokeWidth: 2,
        strokeDashArray: [5, 5],
        selectable: false,
        evented: false,
      } as any
    );
    (line as any).polygonLine = true;
    polygonLines.push(line);
    assignLayerToObject(line, true); // true = measurement
    canvas.value.add(line);
  }

  canvas.value.renderAll();
};

// Complete polygon measurement
const completePolygonMeasurement = () => {
  if (!canvas.value || polygonPoints.length < 3) {
    // Need at least 3 points for a polygon
    clearPolygonMeasurement();
    return;
  }

  // Close the polygon with a line to the first point
  const lastPoint = polygonPoints[polygonPoints.length - 1];
  const firstPoint = polygonPoints[0];
  const closingLine = new Line(
    [lastPoint.x, lastPoint.y, firstPoint.x, firstPoint.y],
    {
      stroke: '#007bff',
      strokeWidth: 2,
      strokeDashArray: [5, 5],
      selectable: false,
      evented: false,
    } as any
  );
  (closingLine as any).polygonLine = true;
  polygonLines.push(closingLine);
  assignLayerToObject(closingLine, true); // true = measurement
  canvas.value.add(closingLine);

  // Calculate area using shoelace formula
  const pixelArea = calculatePolygonArea(polygonPoints);
  const scaleRatio = toolSettings.value.scaleRatio || 100;
  const realArea = (pixelArea / 10000) * (scaleRatio * scaleRatio);

  // Calculate perimeter
  let pixelPerimeter = 0;
  for (let i = 0; i < polygonPoints.length; i++) {
    const p1 = polygonPoints[i];
    const p2 = polygonPoints[(i + 1) % polygonPoints.length];
    const distance = Math.sqrt(
      Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2)
    );
    pixelPerimeter += distance;
  }
  const realPerimeter = (pixelPerimeter / 100) * scaleRatio;

  // Calculate centroid for label placement
  const centroid = { x: 0, y: 0 };
  for (const point of polygonPoints) {
    centroid.x += point.x;
    centroid.y += point.y;
  }
  centroid.x /= polygonPoints.length;
  centroid.y /= polygonPoints.length;

  // Add measurement label
  const unit = toolSettings.value.measurementUnit === 'feet' ? 'ft' : 'm';
  const label = `Area: ${realArea.toFixed(2)} ${unit}²\nPerimeter: ${realPerimeter.toFixed(2)} ${unit}`;

  const text = new Text(label, {
    left: centroid.x,
    top: centroid.y,
    fontSize: 16,
    fill: '#000000',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    padding: 4,
    selectable: false,
    evented: false,
    originX: 'center',
    originY: 'center',
    shadow: 'rgba(0,0,0,0.3) 0px 2px 4px',
  } as any);
  (text as any).polygonLabel = true;

  assignLayerToObject(text, true); // true = measurement
  canvas.value.add(text);

  // Create polygon shape for fill
  const polygon = new Polygon(polygonPoints, {
    fill: 'rgba(0, 123, 255, 0.2)',
    stroke: '#007bff',
    strokeWidth: 2,
    strokeDashArray: [5, 5],
    selectable: true,
  } as any);
  (polygon as any).measurement = 'polygon';
  const polygonId = `polygon-${Date.now()}`;
  (polygon as any).id = polygonId;
  assignLayerToObject(polygon, true); // true = measurement
  canvas.value.add(polygon);
  canvas.value.sendObjectToBack(polygon);

  // Add to measurement history
  addMeasurementToHistory('polygon', label, polygonId);

  // Reset polygon state
  polygonPoints = [];
  polygonLines = [];
  polygonMarkers = [];

  saveHistory();
  markDirty();
  canvas.value.renderAll();
};

// Clear polygon measurement in progress
const clearPolygonMeasurement = () => {
  if (!canvas.value) return;

  // Remove all markers and lines
  for (const marker of polygonMarkers) {
    canvas.value.remove(marker);
  }
  for (const line of polygonLines) {
    canvas.value.remove(line);
  }

  polygonPoints = [];
  polygonLines = [];
  polygonMarkers = [];
  canvas.value.renderAll();
};

// Calculate polygon area using shoelace formula
const calculatePolygonArea = (vertices: { x: number; y: number }[]): number => {
  let area = 0;
  for (let i = 0; i < vertices.length; i++) {
    const j = (i + 1) % vertices.length;
    area += vertices[i].x * vertices[j].y;
    area -= vertices[j].x * vertices[i].y;
  }
  return Math.abs(area / 2);
};

// Handle mouse wheel for zoom
const handleMouseWheel = (opt: any) => {
  if (!canvas.value) return;

  const delta = opt.e.deltaY;
  let zoom = canvas.value.getZoom();
  zoom *= 0.999 ** delta;

  if (zoom > 4) zoom = 4;
  if (zoom < 0.25) zoom = 0.25;

  canvas.value.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);
  zoomLevel.value = zoom;
  opt.e.preventDefault();
  opt.e.stopPropagation();
};

// Tool change handler
const handleToolChange = (tool: AnnotationTool) => {
  currentTool.value = tool;
  
  // Show/hide context panel based on tool
  // Hide for SELECT and ERASER tools (no settings to configure)
  showContextPanel.value = tool !== AnnotationTool.SELECT && tool !== AnnotationTool.ERASER;
  
  if (!canvas.value) return;

  // Deselect objects when switching tools
  canvas.value.discardActiveObject();

  // Toggle drawing mode ahead of pointer events so Fabric's internal handlers will create paths
  if (tool === AnnotationTool.FREEHAND) {
    canvas.value.selection = false;
    // Ensure a PencilBrush exists and is configured
    try {
      const currentBrush = (canvas.value as any).freeDrawingBrush;
      if (!currentBrush || !currentBrush.constructor) {
        (canvas.value as any).freeDrawingBrush = new PencilBrush(canvas.value);
      }
      const brush = (canvas.value as any).freeDrawingBrush;
      brush.color = toolSettings.value.strokeColor;
      brush.width = toolSettings.value.strokeWidth;
      canvas.value.freeDrawingCursor = 'crosshair';
    } catch (e) {
      console.warn('Failed to initialize freeDrawingBrush on tool change', e);
    }
    canvas.value.isDrawingMode = true;
  } else {
    canvas.value.isDrawingMode = false;
    // Enable selection for SELECT and ERASER tools
    canvas.value.selection = tool === AnnotationTool.SELECT || tool === AnnotationTool.ERASER;
  }

  // Set cursor for eraser tool
  if (tool === AnnotationTool.ERASER) {
    canvas.value.defaultCursor = 'pointer';
    canvas.value.hoverCursor = 'pointer';
  } else if (tool !== AnnotationTool.SELECT) {
    canvas.value.defaultCursor = 'crosshair';
    canvas.value.hoverCursor = 'crosshair';
  } else {
    canvas.value.defaultCursor = 'default';
    canvas.value.hoverCursor = 'move';
  }

  canvas.value.renderAll();
};

// Setting change handler
const handleSettingChange = (setting: string, value: any) => {
  (toolSettings.value as any)[setting] = value;
  
  // Re-render grid if grid-related settings change
  if (setting === 'showGrid' || setting === 'gridSize') {
    renderGrid();
  }
  
  // Update active object opacity if opacity changes
  if (setting === 'opacity' && canvas.value) {
    const activeObject = canvas.value.getActiveObject();
    if (activeObject) {
      activeObject.set('opacity', value);
      canvas.value.renderAll();
    }
  }
};

// History management
const saveHistory = () => {
  if (!canvas.value) return;

  // Note: We don't save backgroundImage in history as it should persist across all states
  const json = JSON.stringify(canvas.value.toJSON(['selectable', 'evented']));
  
  // Remove future history if we're not at the end
  if (historyIndex.value < historyStack.value.length - 1) {
    historyStack.value = historyStack.value.slice(0, historyIndex.value + 1);
  }

  historyStack.value.push(json);
  
  // Limit history size
  if (historyStack.value.length > maxHistorySize) {
    historyStack.value.shift();
  } else {
    historyIndex.value++;
  }

  updateHistoryButtons();
  
  console.log('History saved. Canvas has background:', !!canvas.value.backgroundImage);
};

const undo = () => {
  if (!canvas.value || historyIndex.value <= 0) return;

  historyIndex.value--;
  loadHistoryState();
  updateHistoryButtons();
  markDirty();
};

const redo = () => {
  if (!canvas.value || historyIndex.value >= historyStack.value.length - 1) return;

  historyIndex.value++;
  loadHistoryState();
  updateHistoryButtons();
  markDirty();
};

const loadHistoryState = () => {
  if (!canvas.value || historyIndex.value < 0) return;

  const state = historyStack.value[historyIndex.value];
  // Store background before loading to avoid losing it
  const bgImage = backgroundImage.value;
  canvas.value.loadFromJSON(state, () => {
    setTimeout(() => {
      if (bgImage && canvas.value) {
        canvas.value.backgroundImage = bgImage;
        canvas.value.requestRenderAll();
      }
    }, 100);
  });
};

const updateHistoryButtons = () => {
  canUndo.value = historyIndex.value > 0;
  canRedo.value = historyIndex.value < historyStack.value.length - 1;
};

const clearAll = () => {
  if (!canvas.value) return;
  
  notifyConfirm(
    t('annotation.confirmClearAll'),
    () => {
      // Confirmed - clear all annotations
      const bgImage = backgroundImage.value;
      canvas.value!.getObjects().forEach((obj: any) => {
        canvas.value!.remove(obj);
      });
      canvas.value.backgroundImage = bgImage;
      canvas.value!.renderAll();
      saveHistory();
      markDirty();
      notifySuccess(t('annotation.clearedAll'));
    }
  );
};

// Zoom functions
const zoomIn = () => {
  if (!canvas.value) return;
  let zoom = canvas.value.getZoom();
  zoom = Math.min(zoom * 1.2, 4);
  canvas.value.setZoom(zoom);
  zoomLevel.value = zoom;
};

const zoomOut = () => {
  if (!canvas.value) return;
  let zoom = canvas.value.getZoom();
  zoom = Math.max(zoom / 1.2, 0.25);
  canvas.value.setZoom(zoom);
  zoomLevel.value = zoom;
};

const resetZoom = () => {
  if (!canvas.value) return;
  canvas.value.setZoom(1);
  canvas.value.viewportTransform = [1, 0, 0, 1, 0, 0];
  zoomLevel.value = 1;
};

const fitToScreen = () => {
  if (!canvas.value || !backgroundImage.value || !canvasContainer.value) return;

  const containerWidth = canvasContainer.value.clientWidth;
  const containerHeight = canvasContainer.value.clientHeight;
  const imgWidth = backgroundImage.value.width! * backgroundImage.value.scaleX!;
  const imgHeight = backgroundImage.value.height! * backgroundImage.value.scaleY!;

  const zoom = Math.min(containerWidth / imgWidth, containerHeight / imgHeight) * 0.9;
  canvas.value.setZoom(zoom);
  zoomLevel.value = zoom;
};

const fitToWidth = () => {
  if (!canvas.value || !backgroundImage.value || !canvasContainer.value) return;

  const containerWidth = canvasContainer.value.clientWidth;
  const imgWidth = backgroundImage.value.width! * backgroundImage.value.scaleX!;

  const zoom = (containerWidth / imgWidth) * 0.95;
  canvas.value.setZoom(zoom);
  zoomLevel.value = zoom;
  
  // Center vertically
  canvas.value.viewportTransform![5] = 0;
};

const fitToPage = () => {
  if (!canvas.value || !backgroundImage.value || !canvasContainer.value) return;

  const containerWidth = canvasContainer.value.clientWidth;
  const containerHeight = canvasContainer.value.clientHeight;
  const imgWidth = backgroundImage.value.width! * backgroundImage.value.scaleX!;
  const imgHeight = backgroundImage.value.height! * backgroundImage.value.scaleY!;

  const zoom = Math.min(containerWidth / imgWidth, containerHeight / imgHeight);
  canvas.value.setZoom(zoom);
  zoomLevel.value = zoom;
  
  // Center the view
  canvas.value.viewportTransform = [zoom, 0, 0, zoom, 0, 0];
};

const actualSize = () => {
  if (!canvas.value) return;
  canvas.value.setZoom(1);
  canvas.value.viewportTransform = [1, 0, 0, 1, 0, 0];
  zoomLevel.value = 1;
};

const setZoom = (zoom: number) => {
  if (!canvas.value) return;
  const clampedZoom = Math.max(0.25, Math.min(4, zoom));
  canvas.value.setZoom(clampedZoom);
  zoomLevel.value = clampedZoom;
};

// ...existing code...

// Resize canvas
const resizeCanvas = () => {
  if (!canvas.value || !canvasContainer.value) return;

  canvas.value.setWidth(canvasContainer.value.clientWidth);
  canvas.value.setHeight(canvasContainer.value.clientHeight);
  canvas.value.renderAll();
  
  // Re-render grid if enabled
  if (toolSettings.value.showGrid) {
    renderGrid();
  }
};

// Grid rendering
let gridGroup: Group | null = null;

const renderGrid = () => {
  if (!canvas.value) return;
  
  // Remove existing grid
  if (gridGroup) {
    canvas.value.remove(gridGroup);
    gridGroup = null;
  }
  
  if (!toolSettings.value.showGrid) {
    canvas.value.renderAll();
    return;
  }
  
  const gridSize = toolSettings.value.gridSize || 20;
  const width = canvas.value.getWidth();
  const height = canvas.value.getHeight();
  const gridLines: Line[] = [];
  
  // Create vertical lines
  for (let i = 0; i <= width; i += gridSize) {
    const line = new Line([i, 0, i, height], {
      stroke: '#ddd',
      strokeWidth: 1,
      selectable: false,
      evented: false,
    });
    gridLines.push(line);
  }
  
  // Create horizontal lines
  for (let i = 0; i <= height; i += gridSize) {
    const line = new Line([0, i, width, i], {
      stroke: '#ddd',
      strokeWidth: 1,
      selectable: false,
      evented: false,
    });
    gridLines.push(line);
  }
  
  // Create group and add to canvas background
  gridGroup = new Group(gridLines, {
    selectable: false,
    evented: false,
  });
  
  canvas.value.add(gridGroup);
  canvas.value.sendObjectToBack(gridGroup);
  canvas.value.renderAll();
};

// Keyboard shortcuts
const setupKeyboardShortcuts = () => {
  window.addEventListener('keydown', handleKeyDown);
};

const handleKeyDown = (e: KeyboardEvent) => {
  if (!canvas.value) return;

  // Prevent shortcuts when typing in input fields
  const target = e.target as HTMLElement;
  if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
    return;
  }

  // Ctrl+Z - Undo
  if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
    e.preventDefault();
    undo();
  }
  // Ctrl+Shift+Z or Ctrl+Y - Redo
  else if ((e.ctrlKey && e.shiftKey && e.key === 'z') || (e.ctrlKey && e.key === 'y')) {
    e.preventDefault();
    redo();
  }
  // Delete - Remove selected object
  else if (e.key === 'Delete') {
    const activeObject = canvas.value.getActiveObject();
    if (activeObject) {
      canvas.value.remove(activeObject);
      saveHistory();
      markDirty();
    }
  }
  // Ctrl+S - Save
  else if (e.ctrlKey && e.key === 's') {
    e.preventDefault();
    saveAnnotations();
  }
  // ESC - Deselect / Cancel current tool / Clear polygon in progress
  else if (e.key === 'Escape') {
    e.preventDefault();
    if (currentTool.value === AnnotationTool.MEASURE_POLYGON && polygonPoints.length > 0) {
      clearPolygonMeasurement();
    } else {
      canvas.value.discardActiveObject();
      canvas.value.renderAll();
    }
  }
  // Enter - Complete polygon measurement
  else if (e.key === 'Enter' && currentTool.value === AnnotationTool.MEASURE_POLYGON) {
    e.preventDefault();
    completePolygonMeasurement();
  }
  // Tool shortcuts (only if not holding Ctrl)
  else if (!e.ctrlKey && !e.shiftKey && !e.altKey) {
    switch (e.key.toLowerCase()) {
      case 's':
        currentTool.value = AnnotationTool.SELECT;
        break;
      case 'p':
        currentTool.value = AnnotationTool.FREEHAND;
        break;
      case 'l':
        currentTool.value = AnnotationTool.LINE;
        break;
      case 'r':
        currentTool.value = AnnotationTool.RECTANGLE;
        break;
      case 'c':
        currentTool.value = AnnotationTool.CIRCLE;
        break;
      case 'm':
        currentTool.value = AnnotationTool.MEASURE_DISTANCE;
        break;
      case 'a':
        currentTool.value = AnnotationTool.MEASURE_AREA;
        break;
    }
  }
  // Arrow keys - Page navigation
  else if (e.key === 'ArrowLeft' && !e.ctrlKey && !e.shiftKey) {
    e.preventDefault();
    if (currentPage.value > 1) {
      handlePageChange(currentPage.value - 1);
    }
  }
  else if (e.key === 'ArrowRight' && !e.ctrlKey && !e.shiftKey) {
    e.preventDefault();
    if (currentPage.value < totalPages.value) {
      handlePageChange(currentPage.value + 1);
    }
  }
};

// Mark as dirty
const markDirty = () => {
  isDirty.value = true;
  scheduleAutoSave();
};

// Auto-save
const scheduleAutoSave = () => {
  if (autoSaveTimer) {
    clearTimeout(autoSaveTimer);
  }
  autoSaveTimer = setTimeout(() => {
    saveAnnotations(true);
  }, 30000); // Auto-save after 30 seconds
};

// Handle page change
const handlePageChange = async (newPage: number) => {
  if (!canvas.value || newPage === currentPage.value) return;

  // Save current page annotations
  const currentData = JSON.stringify(canvas.value.toJSON(['selectable', 'evented']));
  pageAnnotations.value[currentPage.value] = currentData;

  // Switch to new page
  currentPage.value = newPage;

  // Clear canvas
  canvas.value.clear();

  // Load new page background
  await loadPdfPage(newPage);

  // Load annotations for new page
  if (pageAnnotations.value[newPage]) {
    const pageData = JSON.parse(pageAnnotations.value[newPage]);
    // Store background before loading to avoid losing it
    const bgImage = backgroundImage.value;
    canvas.value.loadFromJSON(pageData, () => {
      if (bgImage && canvas.value) {
        canvas.value.backgroundImage = bgImage;
        canvas.value.renderAll();
      }
    });
  } else {
    // No annotations for this page, ensure background is visible
    if (backgroundImage.value && canvas.value) {
      canvas.value.backgroundImage = backgroundImage.value;
      canvas.value.renderAll();
    }
  }

  updateAnnotationList();
};

// Update annotation list from canvas objects
const updateAnnotationList = () => {
  if (!canvas.value) return;

  const objects = canvas.value.getObjects();
  const items: AnnotationListItem[] = [];

  objects.forEach((obj: any, index: number) => {
    if (obj === backgroundImage.value) return;

    const item: AnnotationListItem = {
      id: obj.id || `annotation-${index}`,
      type: getObjectType(obj),
      pageNumber: totalPages.value > 1 ? currentPage.value : undefined,
      timestamp: obj.timestamp || new Date(),
      color: obj.stroke || obj.fill || '#000000',
      position: { x: obj.left || 0, y: obj.top || 0 },
      text: obj.type === 'i-text' || obj.type === 'text' ? obj.text : undefined,
    };

    items.push(item);
  });

  annotationListItems.value = items;
  updateAnnotationCounts();
};

// Get object type
const getObjectType = (obj: any): AnnotationTool => {
  if (obj.type === 'rect') return AnnotationTool.RECTANGLE;
  if (obj.type === 'circle') return AnnotationTool.CIRCLE;
  if (obj.type === 'line') return AnnotationTool.LINE;
  if (obj.type === 'path') return AnnotationTool.FREEHAND;
  if (obj.measurement === 'distance') return AnnotationTool.MEASURE_DISTANCE;
  if (obj.measurement === 'area') return AnnotationTool.MEASURE_AREA;
  return AnnotationTool.SELECT;
};

// Update annotation counts per page
const updateAnnotationCounts = () => {
  const counts: Record<number, number> = {};
  
  // Count current page
  counts[currentPage.value] = annotationListItems.value.length;
  
  // Count other pages from stored data
  Object.keys(pageAnnotations.value).forEach(pageNumStr => {
    const pageNum = parseInt(pageNumStr);
    if (pageNum !== currentPage.value && pageAnnotations.value[pageNum]) {
      try {
        const data = JSON.parse(pageAnnotations.value[pageNum]);
        counts[pageNum] = data.objects?.length || 0;
      } catch (e) {
        counts[pageNum] = 0;
      }
    }
  });

  annotationCounts.value = counts;
};

// Handle annotation selection
const handleAnnotationSelect = (annotation: AnnotationListItem) => {
  if (!canvas.value) return;
  
  selectedAnnotationId.value = annotation.id;
  
  const objects = canvas.value.getObjects();
  const obj = objects.find((o: any) => (o.id || `annotation-${objects.indexOf(o)}`) === annotation.id);
  
  if (obj) {
    canvas.value.setActiveObject(obj);
    canvas.value.renderAll();
  }
};

// Handle jump to annotation
const handleAnnotationJump = (annotation: AnnotationListItem) => {
  if (!canvas.value) return;

  // Switch to annotation's page if needed
  if (annotation.pageNumber && annotation.pageNumber !== currentPage.value) {
    handlePageChange(annotation.pageNumber);
    // Wait for page to load then select
    setTimeout(() => handleAnnotationSelect(annotation), 500);
    return;
  }

  handleAnnotationSelect(annotation);

  // Center view on annotation
  const obj = canvas.value.getActiveObject();
  if (obj) {
    const zoom = canvas.value.getZoom();
    const vpCenter = canvas.value.getVpCenter();
    canvas.value.absolutePan({
      x: (obj.left || 0) * zoom - vpCenter.x,
      y: (obj.top || 0) * zoom - vpCenter.y,
    });
  }
};

// Handle annotation delete
const handleAnnotationDelete = (annotation: AnnotationListItem) => {
  if (!canvas.value) return;

  const objects = canvas.value.getObjects();
  const obj = objects.find((o: any) => (o.id || `annotation-${objects.indexOf(o)}`) === annotation.id);
  
  if (obj) {
    canvas.value.remove(obj);
    updateAnnotationList();
    saveHistory();
    markDirty();
  }
};

// Add measurement to history
const addMeasurementToHistory = (type: 'distance' | 'area' | 'polygon', value: string, objectId?: string) => {
  measurementHistory.value.push({
    type,
    value,
    pageNumber: totalPages.value > 1 ? currentPage.value : undefined,
    objectId,
  });
};

// Handle measurement selection
const handleMeasurementSelect = (index: number) => {
  selectedMeasurementIndex.value = index;
  // Could optionally highlight the measurement on canvas
};

// Handle measurement deletion
const handleMeasurementDelete = (index: number) => {
  const measurement = measurementHistory.value[index];
  if (measurement.objectId && canvas.value) {
    // Find and remove the measurement object from canvas
    const objects = canvas.value.getObjects();
    const objToRemove = objects.find((obj: any) => obj.id === measurement.objectId);
    if (objToRemove) {
      canvas.value.remove(objToRemove);
      // Also remove associated labels and markers
      const relatedObjects = objects.filter((obj: any) => 
        obj.measurementLabel === measurement.objectId || 
        obj.measurementEndpoint === measurement.objectId ||
        obj.polygonMarker || obj.polygonLine || obj.polygonLabel
      );
      relatedObjects.forEach((obj: any) => canvas.value!.remove(obj));
      saveHistory();
      markDirty();
    }
  }
  measurementHistory.value.splice(index, 1);
  selectedMeasurementIndex.value = undefined;
};

// Clear all measurements
const clearAllMeasurements = () => {
  if (!canvas.value) return;
  
  notifyConfirm(
    t('annotation.confirmClearMeasurements'),
    () => {
      // Confirmed - clear all measurements
      const objects = canvas.value!.getObjects();
      const measurementObjects = objects.filter((obj: any) => 
        obj.measurement || obj.measurementLabel || obj.measurementEndpoint || 
        obj.polygonMarker || obj.polygonLine || obj.polygonLabel
      );
      
      measurementObjects.forEach((obj: any) => canvas.value!.remove(obj));
      
      // Clear measurement history
      measurementHistory.value = [];
      selectedMeasurementIndex.value = undefined;
      
      canvas.value!.renderAll();
      saveHistory();
      markDirty();
      notifySuccess(t('annotation.measurementsCleared'));
    }
  );
};

// Export measurements
const exportMeasurements = () => {
  const report = measurementHistory.value.map((m: any, index: number) => ({
    No: index + 1,
    Type: m.type.charAt(0).toUpperCase() + m.type.slice(1),
    Value: m.value,
    Page: m.pageNumber || 'N/A',
  }));

  const csv = [
    ['No', 'Type', 'Value', 'Page'].join(','),
    ...report.map((r: any) => Object.values(r).map((v: any) => `"${v}"`).join(','))
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv' });
  saveAs(blob, `measurements-${props.fileName}-${new Date().toISOString().split('T')[0]}.csv`);
};

// ========== Layer System Handlers ==========

// Initialize default layers
const initializeLayers = () => {
  if (layers.value.length === 0) {
    const defaultLayers: AnnotationLayer[] = [
      {
        id: 'layer-measurements',
        name: t('annotation.measurementsLayer'),
        visible: true,
        locked: false,
        color: '#007bff',
        opacity: 1,
        order: 3,
        createdAt: new Date(),
      },
      {
        id: 'layer-comments',
        name: t('annotation.commentsLayer'),
        visible: true,
        locked: false,
        color: '#dc3545',
        opacity: 1,
        order: 2,
        createdAt: new Date(),
      },
      {
        id: 'layer-background',
        name: t('annotation.backgroundLayer'),
        visible: true,
        locked: false,
        color: '#6c757d',
        opacity: 1,
        order: 1,
        createdAt: new Date(),
      },
    ];
    layers.value = defaultLayers;
    activeLayerId.value = 'layer-comments';
  }
  updateLayerObjectCounts();
};

// Assign layer to object
const assignLayerToObject = (obj: any, isMeasurement: boolean = false) => {
  if (!obj) return;
  
  // Assign to currently active layer (measurements are no longer forced to measurements layer)
  if (activeLayerId.value) {
    obj.layerId = activeLayerId.value;
  } else {
    // Fallback: if no active layer, use measurements layer for measurements, comments layer for others
    obj.layerId = isMeasurement ? 'layer-measurements' : 'layer-comments';
  }
  
  // Apply layer properties
  const layer = layers.value.find((l: AnnotationLayer) => l.id === obj.layerId);
  if (layer) {
    obj.visible = layer.visible;
    obj.selectable = layer.visible && !layer.locked;
    obj.evented = layer.visible && !layer.locked;
    if (obj.opacity === undefined || obj.opacity === 1) {
      obj.opacity = layer.opacity;
    }
  }
};

// Update object counts for each layer
const updateLayerObjectCounts = () => {
  if (!canvas.value) return;
  
  const objects = canvas.value.getObjects();
  layers.value.forEach((layer: AnnotationLayer) => {
    layer.objectCount = objects.filter((obj: any) => obj.layerId === layer.id).length;
  });
};

// Handle layer creation
const handleLayerCreate = (name: string) => {
  const newLayer: AnnotationLayer = {
    id: `layer-${Date.now()}`,
    name,
    visible: true,
    locked: false,
    color: '#28a745',
    opacity: 1,
    order: Math.max(...layers.value.map((l: AnnotationLayer) => l.order)) + 1,
    createdAt: new Date(),
    objectCount: 0,
  };
  layers.value.push(newLayer);
  activeLayerId.value = newLayer.id;
  markDirty();
};

// Handle layer deletion
const handleLayerDelete = (layerId: string) => {
  if (layers.value.length <= 1) return;
  
  if (!canvas.value) return;
  
  // Remove all objects in this layer
  const objects = canvas.value.getObjects();
  const layerObjects = objects.filter((obj: any) => obj.layerId === layerId);
  layerObjects.forEach((obj: any) => canvas.value!.remove(obj));
  
  // Remove layer
  layers.value = layers.value.filter((l: AnnotationLayer) => l.id !== layerId);
  
  // Update active layer if deleted
  if (activeLayerId.value === layerId) {
    activeLayerId.value = layers.value[0]?.id || null;
  }
  
  saveHistory();
  markDirty();
};

// Handle layer rename
const handleLayerRename = (layerId: string, name: string) => {
  const layer = layers.value.find((l: AnnotationLayer) => l.id === layerId);
  if (layer) {
    layer.name = name;
    markDirty();
  }
};

// Handle layer selection
const handleLayerSelect = (layerId: string) => {
  activeLayerId.value = layerId;
};

// Handle layer visibility toggle
const handleLayerToggleVisibility = (layerId: string) => {
  const layer = layers.value.find((l: AnnotationLayer) => l.id === layerId);
  if (!layer || !canvas.value) return;
  
  layer.visible = !layer.visible;
  
  // Update visibility of all objects in this layer
  const objects = canvas.value.getObjects();
  objects.forEach((obj: any) => {
    if (obj.layerId === layerId) {
      obj.visible = layer.visible;
      obj.selectable = layer.visible && !layer.locked;
    }
  });
  
  canvas.value.renderAll();
  markDirty();
};

// Handle layer lock toggle
const handleLayerToggleLock = (layerId: string) => {
  const layer = layers.value.find((l: AnnotationLayer) => l.id === layerId);
  if (!layer || !canvas.value) return;
  
  layer.locked = !layer.locked;
  
  // Update selectability of all objects in this layer
  const objects = canvas.value.getObjects();
  objects.forEach((obj: any) => {
    if (obj.layerId === layerId) {
      obj.selectable = layer.visible && !layer.locked;
      obj.evented = layer.visible && !layer.locked;
    }
  });
  
  canvas.value.renderAll();
  markDirty();
};

// Handle layer color change
const handleLayerChangeColor = (layerId: string, color: string) => {
  const layer = layers.value.find((l: AnnotationLayer) => l.id === layerId);
  if (layer) {
    layer.color = color;
    markDirty();
  }
};

// Handle layer opacity change
const handleLayerChangeOpacity = (layerId: string, opacity: number) => {
  const layer = layers.value.find((l: AnnotationLayer) => l.id === layerId);
  if (!layer || !canvas.value) return;
  
  layer.opacity = opacity;
  
  // Update opacity of all objects in this layer
  const objects = canvas.value.getObjects();
  objects.forEach((obj: any) => {
    if (obj.layerId === layerId) {
      obj.opacity = opacity;
    }
  });
  
  canvas.value.renderAll();
  markDirty();
};

// Handle layer reorder
const handleLayerReorder = (layerId: string, direction: 'up' | 'down') => {
  const layer = layers.value.find((l: AnnotationLayer) => l.id === layerId);
  if (!layer) return;
  
  const sortedLayers = [...layers.value].sort((a, b) => a.order - b.order);
  const currentIndex = sortedLayers.findIndex((l: AnnotationLayer) => l.id === layerId);
  
  if (direction === 'up' && currentIndex < sortedLayers.length - 1) {
    const temp = sortedLayers[currentIndex].order;
    sortedLayers[currentIndex].order = sortedLayers[currentIndex + 1].order;
    sortedLayers[currentIndex + 1].order = temp;
  } else if (direction === 'down' && currentIndex > 0) {
    const temp = sortedLayers[currentIndex].order;
    sortedLayers[currentIndex].order = sortedLayers[currentIndex - 1].order;
    sortedLayers[currentIndex - 1].order = temp;
  }
  
  if (canvas.value) {
    reorderCanvasObjects();
  }
  markDirty();
};

// Reorder canvas objects based on layer order
const reorderCanvasObjects = () => {
  if (!canvas.value) return;
  
  const objects = canvas.value.getObjects();
  objects.sort((a: any, b: any) => {
    const layerA = layers.value.find((l: AnnotationLayer) => l.id === a.layerId);
    const layerB = layers.value.find((l: AnnotationLayer) => l.id === b.layerId);
    return (layerA?.order || 0) - (layerB?.order || 0);
  });
  
  canvas.value.renderAll();
};

// Handle layer duplicate
const handleLayerDuplicate = (layerId: string) => {
  const sourceLayer = layers.value.find((l: AnnotationLayer) => l.id === layerId);
  if (!sourceLayer || !canvas.value) return;
  
  // Create new layer
  const newLayer: AnnotationLayer = {
    id: `layer-${Date.now()}`,
    name: `${sourceLayer.name} (Copy)`,
    visible: true,
    locked: false,
    color: sourceLayer.color,
    opacity: sourceLayer.opacity,
    order: Math.max(...layers.value.map((l: AnnotationLayer) => l.order)) + 1,
    createdAt: new Date(),
    objectCount: 0,
  };
  layers.value.push(newLayer);
  
  // Clone all objects from source layer
  const objects = canvas.value.getObjects();
  const sourceObjects = objects.filter((obj: any) => obj.layerId === layerId);
  
  sourceObjects.forEach((obj: any) => {
    obj.clone((cloned: any) => {
      cloned.layerId = newLayer.id;
      cloned.left = (cloned.left || 0) + 10;
      cloned.top = (cloned.top || 0) + 10;
      canvas.value!.add(cloned);
    });
  });
  
  updateLayerObjectCounts();
  saveHistory();
  markDirty();
};

// Handle layer merge
const handleLayerMerge = (layerIds: string[]) => {
  if (layerIds.length < 2 || !canvas.value) return;
  
  // Use the first layer as the target
  const targetLayerId = layerIds[0];
  const targetLayer = layers.value.find((l: AnnotationLayer) => l.id === targetLayerId);
  if (!targetLayer) return;
  
  // Move all objects from other layers to target layer
  const objects = canvas.value.getObjects();
  objects.forEach((obj: any) => {
    if (layerIds.includes(obj.layerId) && obj.layerId !== targetLayerId) {
      obj.layerId = targetLayerId;
    }
  });
  
  // Remove merged layers
  layers.value = layers.value.filter((l: AnnotationLayer) => 
    l.id === targetLayerId || !layerIds.includes(l.id)
  );
  
  updateLayerObjectCounts();
  saveHistory();
  markDirty();
};

// Computed property for calculated scale
const calculatedScale = computed(() => {
  if (calibrationPoints.value.length !== 2 || !calibrationDistance.value) {
    return null;
  }

  const pixelDistance = Math.sqrt(
    Math.pow(calibrationPoints.value[1].x - calibrationPoints.value[0].x, 2) +
    Math.pow(calibrationPoints.value[1].y - calibrationPoints.value[0].y, 2)
  );

  const realDistance = parseFloat(calibrationDistance.value);
  if (realDistance <= 0 || pixelDistance <= 0) {
    return null;
  }

  // Calculate scale ratio: how many real-world units per 100 pixels
  const ratio = (realDistance * 100) / pixelDistance;
  const scaleRatio = Math.round(ratio * 10) / 10; // Round to 1 decimal

  return `1:${scaleRatio}`;
});

// Start scale calibration
const startCalibration = () => {
  if (!canvas.value) return;

  // Reset calibration state
  calibrationPoints.value = [];
  calibrationDistance.value = '';
  calibrationUnit.value = toolSettings.value.measurementUnit || 'meters';
  calibrationStep.value = 'first-point';
  isCalibrating.value = true;

  // Clear any existing calibration visuals
  clearCalibrationVisuals();

  // Disable selection during calibration
  canvas.value.selection = false;
};

// Handle calibration click
const handleCalibrationClick = (pointer: { x: number; y: number }) => {
  if (!canvas.value || !isCalibrating.value) return;

  if (calibrationStep.value === 'first-point') {
    // Add first point
    calibrationPoints.value.push({ x: pointer.x, y: pointer.y });
    
    // Add marker
    const marker = new Circle({
      left: pointer.x,
      top: pointer.y,
      radius: 6,
      fill: '#FF6B00',
      stroke: 'white',
      strokeWidth: 2,
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
    } as any);
    calibrationMarkers.value.push(marker);
    canvas.value.add(marker);

    calibrationStep.value = 'second-point';
  } else if (calibrationStep.value === 'second-point') {
    // Add second point
    calibrationPoints.value.push({ x: pointer.x, y: pointer.y });

    // Add marker
    const marker = new Circle({
      left: pointer.x,
      top: pointer.y,
      radius: 6,
      fill: '#FF6B00',
      stroke: 'white',
      strokeWidth: 2,
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
    } as any);
    calibrationMarkers.value.push(marker);
    canvas.value.add(marker);

    // Draw line between points
    const line = new Line(
      [
        calibrationPoints.value[0].x,
        calibrationPoints.value[0].y,
        calibrationPoints.value[1].x,
        calibrationPoints.value[1].y,
      ],
      {
        stroke: '#FF6B00',
        strokeWidth: 3,
        strokeDashArray: [10, 5],
        selectable: false,
        evented: false,
      } as any
    );
    calibrationLine.value = line;
    canvas.value.add(line);

    canvas.value.renderAll();

    // Show dialog to enter distance
    calibrationStep.value = 'enter-distance';
    showCalibrationDialog.value = true;
  }
};

// Apply calibration
const applyCalibration = () => {
  if (!calculatedScale.value || !calibrationDistance.value) return;

  const pixelDistance = Math.sqrt(
    Math.pow(calibrationPoints.value[1].x - calibrationPoints.value[0].x, 2) +
    Math.pow(calibrationPoints.value[1].y - calibrationPoints.value[0].y, 2)
  );

  const realDistance = parseFloat(calibrationDistance.value);
  const ratio = (realDistance * 100) / pixelDistance;

  // Update tool settings
  toolSettings.value.scaleRatio = ratio;
  toolSettings.value.scale = calculatedScale.value;
  toolSettings.value.measurementUnit = calibrationUnit.value;

  // Mark as dirty to trigger save
  markDirty();

  // Clean up
  cancelCalibration();

  // Notify user
  console.log(`Scale calibrated: ${calculatedScale.value}`);
  store.dispatch('notifications/showSuccess', `Scale calibrated: ${calculatedScale.value}`);
};

// Cancel calibration
const cancelCalibration = () => {
  isCalibrating.value = false;
  calibrationStep.value = 'idle';
  showCalibrationDialog.value = false;
  calibrationPoints.value = [];
  calibrationDistance.value = '';
  clearCalibrationVisuals();

  if (canvas.value) {
    canvas.value.selection = true;
  }
};

// Clear calibration visuals
const clearCalibrationVisuals = () => {
  if (!canvas.value) return;

  // Remove markers
  for (const marker of calibrationMarkers.value) {
    canvas.value.remove(marker);
  }
  calibrationMarkers.value = [];

  // Remove line
  if (calibrationLine.value) {
    canvas.value.remove(calibrationLine.value);
    calibrationLine.value = null;
  }

  canvas.value.renderAll();
};

// Export annotation list
const exportAnnotationList = () => {
  const report = annotationListItems.value.map((a: AnnotationListItem) => ({
    Type: a.type,
    Page: a.pageNumber || 'N/A',
    Text: a.text || '',
    Time: new Date(a.timestamp).toLocaleString(),
    Position: `(${Math.round(a.position.x)}, ${Math.round(a.position.y)})`,
  }));

  const csv = [
    ['Type', 'Page', 'Text', 'Time', 'Position'].join(','),
    ...report.map((r: any) => Object.values(r).map((v: any) => `"${v}"`).join(','))
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv' });
  saveAs(blob, `annotations-${props.fileName}-${new Date().toISOString().split('T')[0]}.csv`);
};

// Save annotations
const saveAnnotations = async (isAutoSave = false) => {
  if (!canvas.value || saving.value) return;

  try {
    saving.value = true;

    // Save current page annotations (include layerId property)
    const currentData = JSON.stringify(canvas.value.toJSON(['selectable', 'evented', 'layerId']));
    pageAnnotations.value[currentPage.value] = currentData;

    // Prepare annotation data with tool settings and layer configuration
    let annotationData: string;
    
    if (totalPages.value > 1) {
      // Multi-page format
      const multiPageData: MultiPageAnnotation[] = [];
      Object.keys(pageAnnotations.value).forEach(pageNumStr => {
        const pageNum = parseInt(pageNumStr);
        if (pageAnnotations.value[pageNum]) {
          multiPageData.push({
            pageNumber: pageNum,
            annotationData: pageAnnotations.value[pageNum],
          });
        }
      });
      annotationData = JSON.stringify({
        pages: multiPageData,
        settings: toolSettings.value,
        layers: layers.value,
        activeLayerId: activeLayerId.value
      });
    } else {
      // Single page format - include settings and layers
      annotationData = JSON.stringify({
        canvasData: currentData,
        settings: toolSettings.value,
        layers: layers.value,
        activeLayerId: activeLayerId.value
      });
    }

    await AnnotationService.saveAnnotation(
      props.projectId,
      props.documentId,
      annotationData
    );

    isDirty.value = false;
    
    if (!isAutoSave) {
      store.dispatch('notifications/showSuccess', t('annotation.savedSuccessfully'));
      emit('saved');
    }
  } catch (err: any) {
    console.error('Error saving annotations:', err);
    store.dispatch('notifications/showError', t('annotation.saveFailed'));
  } finally {
    saving.value = false;
  }
};

// Open color picker from context panel
const openColorPicker = () => {
  // Trigger the color picker in the toolbar
  if (toolbarRef.value && (toolbarRef.value as any).toggleColorPicker) {
    (toolbarRef.value as any).toggleColorPicker();
  }
};

// Lifecycle
onMounted(() => {
  console.log('DocumentAnnotator mounted', {
    projectId: props.projectId,
    documentId: props.documentId,
    documentUrl: props.documentUrl,
    fileName: props.fileName,
    fileType: props.fileType
  });
  
  initializeCanvas();
  window.addEventListener('resize', resizeCanvas);
  
  // Get toolbar element reference for context panel positioning
  if (toolbarRef.value) {
    toolbarElement.value = (toolbarRef.value as any).$el;
  }
});

onUnmounted(() => {
  console.log('DocumentAnnotator unmounted');
  
  if (autoSaveTimer) {
    clearTimeout(autoSaveTimer);
  }
  window.removeEventListener('keydown', handleKeyDown);
  window.removeEventListener('resize', resizeCanvas);
  
  if (canvas.value) {
    canvas.value.dispose();
  }
  
  // Cleanup PDF document
  if (pdfDocumentInstance) {
    try {
      pdfDocumentInstance.destroy();
    } catch (e) {
      console.warn('Error destroying PDF document:', e);
    }
    pdfDocumentInstance = null;
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_unref(isMobile))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _cache[12] || (_cache[12] = _createElementVNode("i", { class: "fas fa-desktop mobile-notice__icon" }, null, -1)),
          _createElementVNode("h3", _hoisted_3, _toDisplayString(_ctx.$t('annotation.desktopOnly')), 1),
          _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.$t('annotation.desktopOnlyMessage')), 1),
          _createVNode(AppButton, {
            variant: "outline",
            size: "small",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close')))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('common.close')), 1)
            ]),
            _: 1
          })
        ]))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("h3", _hoisted_7, [
                _cache[13] || (_cache[13] = _createElementVNode("i", { class: "fas fa-pencil-alt" }, null, -1)),
                _createTextVNode(" " + _toDisplayString(_ctx.fileName), 1)
              ]),
              (isDirty.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_8, [
                    _cache[14] || (_cache[14] = _createElementVNode("i", { class: "fas fa-circle" }, null, -1)),
                    _createTextVNode(" " + _toDisplayString(_ctx.$t('annotation.unsavedChanges')), 1)
                  ]))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_9, [
              _createVNode(AppButton, {
                variant: "outline",
                size: "small",
                onClick: _cache[1] || (_cache[1] = ($event: any) => (showAnnotationList.value = !showAnnotationList.value))
              }, {
                default: _withCtx(() => [
                  _createElementVNode("i", {
                    class: _normalizeClass(showAnnotationList.value ? 'fas fa-list-check' : 'fas fa-list')
                  }, null, 2),
                  _createTextVNode(" " + _toDisplayString(_ctx.$t('annotation.annotationList')), 1)
                ]),
                _: 1
              }),
              _createVNode(AppButton, {
                variant: "outline",
                size: "small",
                onClick: _cache[2] || (_cache[2] = ($event: any) => (showMeasurementHistory.value = !showMeasurementHistory.value))
              }, {
                default: _withCtx(() => [
                  _createElementVNode("i", {
                    class: _normalizeClass(showMeasurementHistory.value ? 'fas fa-ruler-combined' : 'fas fa-ruler')
                  }, null, 2),
                  _createTextVNode(" " + _toDisplayString(_ctx.$t('annotation.measurements')), 1)
                ]),
                _: 1
              }),
              _createVNode(AppButton, {
                variant: "outline",
                size: "small",
                onClick: _cache[3] || (_cache[3] = ($event: any) => (showLayerPanel.value = !showLayerPanel.value))
              }, {
                default: _withCtx(() => [
                  _createElementVNode("i", {
                    class: _normalizeClass(showLayerPanel.value ? 'fas fa-layer-group' : 'fas fa-layer-group')
                  }, null, 2),
                  _createTextVNode(" " + _toDisplayString(_ctx.$t('annotation.layers')), 1)
                ]),
                _: 1
              }),
              _createVNode(AppButton, {
                variant: "outline",
                size: "small",
                onClick: _cache[4] || (_cache[4] = ($event: any) => (_ctx.$emit('close'))),
                disabled: saving.value
              }, {
                default: _withCtx(() => [
                  _cache[15] || (_cache[15] = _createElementVNode("i", { class: "fas fa-times" }, null, -1)),
                  _createTextVNode(" " + _toDisplayString(_ctx.$t('common.cancel')), 1)
                ]),
                _: 1
              }, 8, ["disabled"]),
              _createVNode(AppButton, {
                variant: "primary",
                size: "small",
                onClick: saveAnnotations,
                loading: saving.value,
                disabled: !isDirty.value
              }, {
                default: _withCtx(() => [
                  _cache[16] || (_cache[16] = _createElementVNode("i", { class: "fas fa-save" }, null, -1)),
                  _createTextVNode(" " + _toDisplayString(_ctx.$t('common.save')), 1)
                ]),
                _: 1
              }, 8, ["loading", "disabled"])
            ])
          ]),
          (!showZoomPanel.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                class: "reopen-zoom-btn",
                title: _ctx.$t('annotation.showZoomPanel'),
                onClick: _cache[5] || (_cache[5] = ($event: any) => (showZoomPanel.value = true))
              }, [...(_cache[17] || (_cache[17] = [
                _createElementVNode("i", { class: "fas fa-search" }, null, -1)
              ]))], 8, _hoisted_10))
            : _createCommentVNode("", true),
          _createVNode(AnnotationToolbar, {
            ref_key: "toolbarRef",
            ref: toolbarRef,
            "current-tool": currentTool.value,
            settings: toolSettings.value,
            "can-undo": canUndo.value,
            "can-redo": canRedo.value,
            onToolChange: handleToolChange,
            onSettingChange: handleSettingChange,
            onUndo: undo,
            onRedo: redo,
            onClearAll: clearAll,
            onCalibrateScale: startCalibration
          }, null, 8, ["current-tool", "settings", "can-undo", "can-redo"]),
          _createVNode(AnnotationContextPanel, {
            "current-tool": currentTool.value,
            settings: toolSettings.value,
            "is-visible": showContextPanel.value,
            "anchor-element": toolbarElement.value,
            onSettingChange: handleSettingChange,
            onCalibrateScale: startCalibration,
            onOpenColorPicker: openColorPicker,
            onClose: _cache[6] || (_cache[6] = ($event: any) => (showContextPanel.value = false))
          }, null, 8, ["current-tool", "settings", "is-visible", "anchor-element"]),
          _createElementVNode("div", _hoisted_11, [
            (totalPages.value > 1)
              ? (_openBlock(), _createBlock(PDFPageNavigator, {
                  key: 0,
                  "total-pages": totalPages.value,
                  "current-page": currentPage.value,
                  "annotation-counts": annotationCounts.value,
                  "document-url": _ctx.documentUrl,
                  onPageChange: handlePageChange
                }, null, 8, ["total-pages", "current-page", "annotation-counts", "document-url"]))
              : _createCommentVNode("", true),
            _createElementVNode("div", {
              class: "annotator-canvas-container",
              ref_key: "canvasContainer",
              ref: canvasContainer
            }, [
              (loading.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                    _cache[18] || (_cache[18] = _createElementVNode("div", { class: "spinner" }, null, -1)),
                    _createElementVNode("p", null, _toDisplayString(_ctx.$t('common.loading')) + "...", 1)
                  ]))
                : (error.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                      _cache[19] || (_cache[19] = _createElementVNode("i", { class: "fas fa-exclamation-triangle" }, null, -1)),
                      _createElementVNode("p", null, _toDisplayString(error.value), 1),
                      _createElementVNode("button", {
                        onClick: initializeCanvas,
                        class: "retry-button"
                      }, "Retry")
                    ]))
                  : _createCommentVNode("", true),
              _createElementVNode("canvas", {
                ref_key: "fabricCanvas",
                ref: fabricCanvas,
                class: "annotator-canvas"
              }, null, 512),
              (totalPages.value > 1)
                ? (_openBlock(), _createElementBlock("div", _hoisted_14, " Page " + _toDisplayString(currentPage.value) + " / " + _toDisplayString(totalPages.value), 1))
                : _createCommentVNode("", true)
            ], 512),
            (showAnnotationList.value)
              ? (_openBlock(), _createBlock(AnnotationListPanel, {
                  key: 1,
                  annotations: annotationListItems.value,
                  "selected-annotation-id": selectedAnnotationId.value,
                  onAnnotationSelect: handleAnnotationSelect,
                  onAnnotationJump: handleAnnotationJump,
                  onAnnotationDelete: handleAnnotationDelete,
                  onExportList: exportAnnotationList
                }, null, 8, ["annotations", "selected-annotation-id"]))
              : _createCommentVNode("", true),
            (showMeasurementHistory.value)
              ? (_openBlock(), _createBlock(MeasurementHistoryPanel, {
                  key: 2,
                  measurements: measurementHistory.value,
                  "selected-index": selectedMeasurementIndex.value,
                  onMeasurementSelect: handleMeasurementSelect,
                  onMeasurementDelete: handleMeasurementDelete,
                  onClearAll: clearAllMeasurements,
                  onExport: exportMeasurements
                }, null, 8, ["measurements", "selected-index"]))
              : _createCommentVNode("", true),
            (showLayerPanel.value)
              ? (_openBlock(), _createBlock(LayerPanel, {
                  key: 3,
                  layers: layers.value,
                  "active-layer-id": activeLayerId.value,
                  onLayerCreate: handleLayerCreate,
                  onLayerDelete: handleLayerDelete,
                  onLayerRename: handleLayerRename,
                  onLayerSelect: handleLayerSelect,
                  onLayerToggleVisibility: handleLayerToggleVisibility,
                  onLayerToggleLock: handleLayerToggleLock,
                  onLayerChangeColor: handleLayerChangeColor,
                  onLayerChangeOpacity: handleLayerChangeOpacity,
                  onLayerReorder: handleLayerReorder,
                  onLayerDuplicate: handleLayerDuplicate,
                  onLayerMerge: handleLayerMerge,
                  onClose: _cache[7] || (_cache[7] = ($event: any) => (showLayerPanel.value = false))
                }, null, 8, ["layers", "active-layer-id"]))
              : _createCommentVNode("", true)
          ]),
          _createVNode(AnnotationControls, {
            zoom: zoomLevel.value,
            "is-visible": showZoomPanel.value,
            onZoomIn: zoomIn,
            onZoomOut: zoomOut,
            onZoomReset: resetZoom,
            onZoomTo: setZoom,
            onFitToScreen: fitToScreen,
            onFitToWidth: fitToWidth,
            onFitToPage: fitToPage,
            onActualSize: actualSize,
            onClose: _cache[8] || (_cache[8] = ($event: any) => (showZoomPanel.value = false))
          }, null, 8, ["zoom", "is-visible"]),
          (showCalibrationDialog.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 1,
                class: "modal-overlay",
                onClick: cancelCalibration
              }, [
                _createElementVNode("div", {
                  class: "calibration-dialog",
                  onClick: _cache[11] || (_cache[11] = _withModifiers(() => {}, ["stop"]))
                }, [
                  _createElementVNode("div", _hoisted_15, [
                    _createElementVNode("h3", _hoisted_16, [
                      _cache[20] || (_cache[20] = _createElementVNode("i", {
                        class: "fas fa-ruler-combined",
                        style: {"color":"#333 !important"}
                      }, null, -1)),
                      _createTextVNode(" " + _toDisplayString(_ctx.$t('annotation.scaleCalibration')), 1)
                    ]),
                    _createElementVNode("button", {
                      class: "btn-close",
                      onClick: cancelCalibration
                    }, [...(_cache[21] || (_cache[21] = [
                      _createElementVNode("i", { class: "fas fa-times" }, null, -1)
                    ]))])
                  ]),
                  _createElementVNode("div", _hoisted_17, [
                    _createElementVNode("p", _hoisted_18, _toDisplayString(_ctx.$t('annotation.calibrationInstruction')), 1),
                    _createElementVNode("div", _hoisted_19, [
                      _createElementVNode("label", _hoisted_20, _toDisplayString(_ctx.$t('annotation.knownDistance')), 1),
                      _createElementVNode("div", _hoisted_21, [
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((calibrationDistance).value = $event)),
                          type: "number",
                          min: "0",
                          step: "0.01",
                          placeholder: _ctx.$t('annotation.enterDistance'),
                          class: "calibration-form-input",
                          style: {"background":"#fff !important","color":"#222 !important","border":"2px solid #ccc !important"},
                          onKeyup: _withKeys(applyCalibration, ["enter"])
                        }, null, 40, _hoisted_22), [
                          [_vModelText, calibrationDistance.value]
                        ]),
                        _withDirectives(_createElementVNode("select", {
                          "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((calibrationUnit).value = $event)),
                          class: "calibration-unit-select",
                          style: {"background":"#fff !important","color":"#222 !important","border":"2px solid #ccc !important"}
                        }, [
                          _createElementVNode("option", _hoisted_23, _toDisplayString(_ctx.$t('annotation.meters')), 1),
                          _createElementVNode("option", _hoisted_24, _toDisplayString(_ctx.$t('annotation.feet')), 1)
                        ], 512), [
                          [_vModelSelect, calibrationUnit.value]
                        ])
                      ])
                    ]),
                    (calculatedScale.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                          _cache[22] || (_cache[22] = _createElementVNode("i", {
                            class: "fas fa-info-circle",
                            style: {"color":"#1565c0 !important"}
                          }, null, -1)),
                          _createElementVNode("span", _hoisted_26, [
                            _createTextVNode(_toDisplayString(_ctx.$t('annotation.detectedScale')) + ": ", 1),
                            _createElementVNode("strong", _hoisted_27, _toDisplayString(calculatedScale.value), 1)
                          ])
                        ]))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("div", _hoisted_28, [
                    _createVNode(AppButton, {
                      variant: "outline",
                      onClick: cancelCalibration
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('common.cancel')), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(AppButton, {
                      variant: "primary",
                      onClick: applyCalibration,
                      disabled: !calibrationDistance.value || parseFloat(calibrationDistance.value) <= 0
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('annotation.applyScale')), 1)
                      ]),
                      _: 1
                    }, 8, ["disabled"])
                  ])
                ])
              ]))
            : _createCommentVNode("", true),
          (isCalibrating.value && calibrationStep.value !== 'enter-distance')
            ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
                _createElementVNode("div", _hoisted_30, [
                  _cache[24] || (_cache[24] = _createElementVNode("i", { class: "fas fa-mouse-pointer" }, null, -1)),
                  (calibrationStep.value === 'first-point')
                    ? (_openBlock(), _createElementBlock("p", _hoisted_31, _toDisplayString(_ctx.$t('annotation.clickFirstPoint')), 1))
                    : (calibrationStep.value === 'second-point')
                      ? (_openBlock(), _createElementBlock("p", _hoisted_32, _toDisplayString(_ctx.$t('annotation.clickSecondPoint')), 1))
                      : _createCommentVNode("", true),
                  _createElementVNode("button", {
                    class: "btn-cancel-calibration",
                    onClick: cancelCalibration
                  }, [
                    _cache[23] || (_cache[23] = _createElementVNode("i", { class: "fas fa-times" }, null, -1)),
                    _createTextVNode(" " + _toDisplayString(_ctx.$t('common.cancel')), 1)
                  ])
                ])
              ]))
            : _createCommentVNode("", true)
        ], 64))
  ]))
}
}

})